/*
 * @Author: Ryan
 * @Date: 2025-12-29
 * @Description: Real-world use cases section with accordion-style tabs and video player
 */
import Image from 'next/image';
import React, { useMemo, useRef, useState } from 'react';

import { useImagePreload } from '@/hooks/useImagePreload';

interface UseCase {
  id: string;
  title: string;
  description: string;
  videoUrl: string;
  posterUrl: string;
}

const useCases: UseCase[] = [
  {
    id: 'audiobooks-podcasts',
    title: 'Audiobooks & Podcasts',
    description:
      "Turn written stories into immersive audio experiences. Clone the author's or narrator's voice for authentic audiobooks and podcasts without long studio sessions. Add avatars for video podcasts and use translation to reach global listeners.",
    videoUrl:
      'https://assets.falcocut.ai/ai-voice-cloning/usecase-audiobooks-podcasts.mp4',
    posterUrl:
      'https://assets.falcocut.ai/ai-voice-cloning/usecase-audiobooks-podcasts-poster.webp',
  },
  {
    id: 'marketing-advertising',
    title: 'Marketing & Advertising',
    description:
      'Produce product videos, ads, and social content with your cloned voice and avatar. Launch multilingual campaigns quickly by auto-translating your script while keeping a consistent voice identity across markets.',
    videoUrl:
      'https://assets.falcocut.ai/ai-voice-cloning/usecase-marketing-advertising.mp4',
    posterUrl:
      'https://assets.falcocut.ai/ai-voice-cloning/usecase-marketing-advertising-poster.webp',
  },
  {
    id: 'education-elearning',
    title: 'Education & E-learning',
    description:
      'Create training modules and online courses with lifelike narration in your own voice. Use avatars to teach visually and translate into multiple languages to engage international learners without re-recording.',
    videoUrl:
      'https://assets.falcocut.ai/ai-voice-cloning/usecase-education-elearning.mp4',
    posterUrl:
      'https://assets.falcocut.ai/ai-voice-cloning/usecase-education-elearning-poster.webp',
  },
  {
    id: 'corporate-communication',
    title: 'Corporate Communication',
    description:
      'Leaders can share company updates in their own cloned voice, paired with avatars for professional presentation. Translate messages instantly for different regions, ensuring clarity and connection across global teams.',
    videoUrl:
      'https://assets.falcocut.ai/ai-voice-cloning/usecase-corporate-communication.mp4',
    posterUrl:
      'https://assets.falcocut.ai/ai-voice-cloning/usecase-corporate-communication-poster.webp',
  },
  {
    id: 'customer-support',
    title: 'Customer Support & Experience',
    description:
      'Deliver personalized customer interactions with your cloned voice. Automate FAQs, support messages, and onboarding guides in multiple languages — making support more human and relatable.',
    videoUrl: 'https://assets.falcocut.ai/ai-voice-cloning/usecase-customer-support.mp4',
    posterUrl:
      'https://assets.falcocut.ai/ai-voice-cloning/usecase-customer-support-poster.webp',
  },
];

const PlayButton: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="64"
    height="64"
    viewBox="0 0 64 64"
    fill="none"
  >
    <rect x="1" y="1" width="62" height="62" rx="31" fill="white" fillOpacity="0.1" />
    <rect x="1" y="1" width="62" height="62" rx="31" stroke="white" strokeWidth="2" />
    <path
      d="M41.5038 30.3359C42.6913 31.1275 42.6913 32.8725 41.5038 33.6641L29.1094 41.9271C27.7803 42.8131 26 41.8603 26 40.2629L26 23.7371C26 22.1397 27.7803 21.1869 29.1094 22.0729L41.5038 30.3359Z"
      fill="white"
    />
  </svg>
);

interface UseCaseTabProps {
  useCase: UseCase;
  isActive: boolean;
  onClick: () => void;
}

const UseCaseTab: React.FC<UseCaseTabProps> = ({ useCase, isActive, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-2xl transition-colors duration-300 p-4 pad:p-5 ${
        isActive
          ? 'bg-[#7C3AED] text-white'
          : 'bg-[#F5F5F7] text-gray-700 hover:bg-[#EBEBED]'
      }`}
    >
      <h3
        className={`font-semibold text-base pad:text-base ${isActive ? 'text-white' : 'text-gray-800'}`}
      >
        {useCase.title}
      </h3>
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isActive ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <p className="text-sm pad:text-base text-white/90 leading-relaxed">
            {useCase.description}
          </p>
        </div>
      </div>
    </button>
  );
};

const UseCasesSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const activeUseCase = useCases[activeIndex];

  // 预加载所有封面图片
  const posterUrls = useMemo(() => useCases.map((uc) => uc.posterUrl), []);
  useImagePreload(posterUrls);

  const handleTabClick = (index: number) => {
    setActiveIndex(index);
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const handlePlayClick = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleVideoClick = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const handleVideoEnded = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
    setIsPlaying(false);
  };

  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Real-World Use Cases for AI Voice Cloning
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          FalcoCut AI Voice Cloning is built for creators, businesses, and professionals
          who need to scale their voice content. From YouTube videos to corporate
          training, discover how voice cloning transforms content production across
          industries.
        </p>

        {/* Content */}
        <div className="flex flex-col pad:flex-row gap-6 pad:gap-8 sm:gap-12 items-stretch">
          {/* Left - Video */}
          <div className="w-full pad:w-[55%] flex items-center justify-center">
            <div className="relative w-full rounded-2xl overflow-hidden bg-black aspect-[4/3]">
              {/* 封面图片 - 始终渲染，通过 opacity 控制显隐实现平滑过渡 */}
              <Image
                src={activeUseCase.posterUrl}
                alt={activeUseCase.title}
                fill
                className={`object-cover transition-opacity duration-300 ${isPlaying ? 'opacity-0' : 'opacity-100'}`}
                sizes="(max-width: 768px) 100vw, 50vw"
              />
              {/* 视频元素 - 始终渲染，通过 opacity 控制显隐实现平滑过渡 */}
              <video
                ref={videoRef}
                key={activeUseCase.videoUrl}
                src={activeUseCase.videoUrl}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${isPlaying ? 'opacity-100' : 'opacity-0'}`}
                onEnded={handleVideoEnded}
                onClick={handleVideoClick}
                playsInline
              />
              {/* Play Button Overlay - 带过渡效果 */}
              <button
                type="button"
                onClick={handlePlayClick}
                className={`absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-all duration-300 cursor-pointer z-10 ${isPlaying ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
                aria-label="Play video"
              >
                <PlayButton />
              </button>
            </div>
          </div>

          {/* Right - Tabs */}
          <div className="w-full pad:w-[45%] flex flex-col gap-3 pad:gap-4">
            {useCases.map((useCase, index) => (
              <UseCaseTab
                key={useCase.id}
                useCase={useCase}
                isActive={index === activeIndex}
                onClick={() => handleTabClick(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;
